import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, Phone, Star, Wallet, MapPin, Bike, Loader2, Navigation, Store, Heart, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Section, StatusBadge, Stepper, LoadingBlock, EmptyState } from "../../components/app/ui";
import { DriverCard, PayDialog, RateDialog } from "../../components/app/RideWidgets";
import RideMap from "../../components/app/RideMap";
import useRoadRoute from "../../components/app/useRoadRoute";
import { Receipt } from "lucide-react";
import ReportDialog from "../../components/app/ReportDialog";
import SearchingPulse from "../../components/app/SearchingPulse";
import { ArrivedWaiting } from "../../components/app/ArrivedWaiting";
import RefundTracker from "../../components/app/RefundTracker";
import { useAuth } from "../../context/AuthContext";
import { foodApi, restaurantApi, inr, formatDate, errorMessage, titleCase } from "../../lib/api";
import { favApi } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { openCheckout } from "../../lib/razorpay";
import ChatUnreadBadge from "../../components/app/ChatUnreadBadge";

const WEEKDAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
function OpeningHours({ hours }) {
  const [open, setOpen] = useState(false);
  if (!hours || typeof hours !== "object") return null;
  return (
    <div className="mt-2" data-testid="order-restaurant-hours">
      <button onClick={() => setOpen((v) => !v)} className="text-xs font-semibold text-brand hover:underline" data-testid="order-hours-toggle">
        {open ? "Hide opening hours" : "View opening hours"}
      </button>
      {open && (
        <dl className="mt-2 space-y-1">
          {WEEKDAYS.map(([k, label]) => {
            const d = hours[k];
            const txt = !d || d.closed ? "Closed" : `${d.open || "--"} – ${d.close || "--"}`;
            return (
              <div key={k} className="flex justify-between text-xs">
                <dt className="text-ink-muted">{label}</dt>
                <dd className={!d || d.closed ? "text-ink-muted" : "text-ink"}>{txt}</dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}


export const ORDER_STEPS = [
  { key: "placed", label: "Waiting for restaurant" },
  { key: "finding_partner", label: "Finding partner" },
  { key: "accepted", label: "Accepted" },
  { key: "preparing", label: "Preparing" },
  { key: "ready", label: "Ready" },
  { key: "out_for_delivery", label: "On the way" },
  { key: "delivered", label: "Delivered" },
];
const NEXT = { placed: "accepted", accepted: "preparing", preparing: "ready" };
export const CANCEL_REASONS = ["Out of stock", "Too busy", "Closing soon", "Item unavailable", "Other"];
const NEXT_PICKUP = { placed: "accepted", accepted: "preparing", preparing: "ready", ready: "delivered" };

export const PICKUP_ORDER_STEPS = [
  { key: "placed", label: "Confirming" },
  { key: "accepted", label: "Accepted" },
  { key: "ready", label: "Ready for pickup" },
  { key: "delivered", label: "Collected" },
];

export function OrderStatusControls({ order, onChange, setStatus = (o, s, extra) => foodApi.orderStatus(o.order_id, s, undefined, undefined, extra) }) {
  const [busy, setBusy] = useState(false);
  const isPickup = order.order_type === "pickup";
  const next = isPickup ? NEXT_PICKUP[order.status] : NEXT[order.status];
  const nextLabel = order.status === "placed" ? "Accept order" : next === "preparing" ? "Start preparing" : next === "delivered" ? "Mark collected" : `Mark ${titleCase(next || "")}`;
  const [prep, setPrep] = useState(order.pickup_prep_min || 20);
  const [code, setCode] = useState("");
  const accepting = order.status === "placed";
  const collecting = isPickup && next === "delivered";
  // Live rider availability so the restaurant never accepts a delivery order with no rider online.
  const [partnersOnline, setPartnersOnline] = useState(null);
  useEffect(() => {
    let alive = true;
    if (accepting && !isPickup) {
      restaurantApi.partnerAvailability(order.order_id).then((d) => { if (alive) setPartnersOnline(d.online_partners); }).catch(() => {});
    } else { setPartnersOnline(null); }
    return () => { alive = false; };
  }, [accepting, isPickup, order.order_id]);
  const set = async (status, extra) => {
    setBusy(true);
    try { const u = await setStatus(order, status, extra); onChange(u); toast.success(u._pending ? `Marked ${titleCase(status)} — will sync when back online` : `Order marked ${titleCase(status)}`); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const [reasonMode, setReasonMode] = useState(null); // 'rejected' | 'cancelled'
  const [reasonPick, setReasonPick] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const openReason = (mode) => { setReasonMode(mode); setReasonPick(""); setReasonNote(""); };
  const submitReason = async () => {
    const reason = (reasonPick === "Other" ? reasonNote.trim() : reasonPick) + (reasonPick !== "Other" && reasonNote.trim() ? ` — ${reasonNote.trim()}` : "");
    if (!reason) { toast.error("Pick a reason"); return; }
    const mode = reasonMode;
    setReasonMode(null);
    setBusy(true);
    try { const u = await foodApi.orderStatus(order.order_id, mode, reason); onChange(u); toast.success(mode === "rejected" ? "Order rejected" : "Order cancelled"); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (["delivered", "cancelled", "rejected"].includes(order.status)) return null;
  return (
    <div className="space-y-3" data-testid="order-status-controls">
      <Dialog open={!!reasonMode} onOpenChange={(o) => { if (!o) setReasonMode(null); }}>
        <DialogContent data-testid="cancel-reason-dialog">
          <DialogHeader><DialogTitle>{reasonMode === "rejected" ? "Reject this order" : "Cancel this order"}</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-soft">Pick a reason — it's shared with the customer.</p>
          <div className="flex flex-wrap gap-2 py-1">
            {CANCEL_REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setReasonPick(r)} className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${reasonPick === r ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:bg-surface-200"}`} data-testid={`cancel-reason-${r.toLowerCase().replace(/\s+/g, "-")}`}>{r}</button>
            ))}
          </div>
          <input value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} placeholder={reasonPick === "Other" ? "Type the reason" : "Add a note (optional)"} className="h-10 w-full rounded-lg border border-surface-300 px-3 text-sm" data-testid="cancel-reason-note" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonMode(null)} className="rounded-full">Keep order</Button>
            <Button onClick={submitReason} disabled={busy || !reasonPick} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="cancel-reason-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {reasonMode === "rejected" ? "Reject order" : "Cancel order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {accepting && !isPickup && partnersOnline !== null && (
        <div className={`rounded-xl px-3 py-2 text-sm font-semibold ${partnersOnline > 0 ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`} data-testid="partner-availability">
          {partnersOnline > 0
            ? `${partnersOnline} delivery partner${partnersOnline === 1 ? "" : "s"} online now`
            : "No delivery partners are online right now — the customer may be offered self-pickup or a refund if none accepts."}
        </div>
      )}
      {accepting && (
        <div className="rounded-xl bg-surface-100 p-3" data-testid="accept-prep-picker">
          <p className="mb-2 text-xs font-semibold text-ink-soft">{isPickup ? "Ready for pickup in" : "Ready in"}</p>
          <div className="flex flex-wrap gap-2">
            {[10, 15, 20, 30, 45].map((m) => (
              <button key={m} type="button" onClick={() => setPrep(m)} className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${prep === m ? "bg-brand text-white" : "bg-white text-ink-soft hover:bg-surface-200"}`} data-testid={`prep-${m}`}>{m} min</button>
            ))}
          </div>
        </div>
      )}
      {collecting && (
        <div className="rounded-xl bg-amber-50 p-3" data-testid="collect-code-entry">
          <label className="mb-1 block text-xs font-semibold text-amber-800" htmlFor="collect-code">Enter the customer's 4-digit collection code</label>
          <input id="collect-code" inputMode="numeric" maxLength={4} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" className="h-10 w-24 rounded-lg border border-amber-300 px-3 text-center text-lg tracking-[0.3em]" data-testid="collect-code-input" />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {next && <Button onClick={() => set(next, accepting ? { prep_min: prep } : collecting ? { code } : undefined)} disabled={busy || (collecting && code.length !== 4)} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`order-advance-${next}`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {nextLabel}</Button>}
        {["placed", "accepted"].includes(order.status) && <Button variant="outline" onClick={() => openReason("rejected")} disabled={busy} className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="order-reject">Reject</Button>}
        <Button variant="ghost" onClick={() => openReason("cancelled")} disabled={busy} className="rounded-full text-red-700 hover:bg-red-50" data-testid="order-cancel">Cancel order</Button>
      </div>
    </div>
  );
}

function AwaitingPayment({ order, onChange, user, isCustomer }) {
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const rp = await foodApi.createPayment(order.order_id);
      const res = await openCheckout({ order: rp, amount: order.total, user, description: `Order from ${order.restaurant_name}` });
      setVerifying(true);
      const u = await foodApi.verifyPayment(order.order_id, { razorpay_order_id: res.razorpay_order_id || rp.order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null, method: "online" });
      onChange(u); toast.success("Payment successful — order placed!");
    } catch (e) { if (!e?.cancelled) toast.error(errorMessage(e, "Payment failed")); } finally { setBusy(false); setVerifying(false); }
  };
  const abandon = async () => { if (!window.confirm("Cancel this order? You haven't paid, so nothing will be charged.")) return; try { onChange(await foodApi.orderStatus(order.order_id, "cancelled")); toast("Order cancelled"); } catch (e) { toast.error(errorMessage(e)); } };
  if (verifying) return (
    <div className="rounded-xl bg-brand-50 px-4 py-6" data-testid="payment-confirming">
      <SearchingPulse title="Confirming your payment…" subtitle="Hang tight — we're placing your order with the restaurant." icon={<Wallet className="h-6 w-6" />} testId="payment-confirming-pulse" />
    </div>
  );
  return (
    <div className="rounded-xl bg-brand-50 px-4 py-4 text-sm" data-testid="awaiting-payment">
      <p className="font-semibold text-ink">Payment pending</p>
      <p className="mt-1 text-ink-soft">This order reaches the restaurant only after your online payment is confirmed.</p>
      {isCustomer && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={pay} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="complete-payment">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Wallet className="mr-2 h-4 w-4" /> Pay {inr(order.total)} now</Button>
          <Button variant="ghost" onClick={abandon} className="rounded-full text-red-700 hover:bg-red-50">Cancel order</Button>
        </div>
      )}
    </div>
  );
}

function CustomerCancel({ order, onChange }) {
  const [busy, setBusy] = useState(false);
  const preAccept = ["awaiting_payment", "placed"].includes(order.status);
  const prepaid = order.payment_status === "paid";
  const isCod = (order.payment_method || "cash") === "cash";
  // Cash orders can't be cancelled by the customer once the restaurant accepts.
  if (isCod && !preAccept && ["finding_partner", "accepted", "preparing"].includes(order.status)) {
    return (
      <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="cod-cancel-blocked">
        The restaurant has accepted this cash order, so it can't be cancelled here. Please call the restaurant and ask them to cancel it.
      </p>
    );
  }
  const cancel = async () => {
    const msg = preAccept
      ? prepaid
        ? "Cancel this order? The restaurant hasn't accepted yet, so you'll be refunded in full."
        : "Cancel this order? The restaurant hasn't accepted it yet."
      : "Cancel this order? The restaurant has already accepted it, so this cannot be refunded.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { onChange(await foodApi.orderStatus(order.order_id, "cancelled")); toast.success(preAccept && prepaid ? "Order cancelled — refund initiated" : "Order cancelled"); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return <Button variant="ghost" onClick={cancel} disabled={busy} className="rounded-full text-red-700 hover:bg-red-50" data-testid="order-cancel">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cancel order</Button>;
}

function NoPartnerChoice({ order, onChange }) {
  const [busy, setBusy] = useState("");
  const fee = Number(order.no_partner_fee_refund ?? order.delivery_fee ?? 0);
  const bonus = Number(order.no_partner_bonus ?? 0);
  const prepaid = order.payment_status === "paid";
  const payable = Number(order.no_partner_pickup_payable ?? Math.max(0, Number(order.total || 0) - fee - bonus));
  const act = async (which) => {
    setBusy(which);
    try {
      const u = which === "pickup" ? await foodApi.selfPickup(order.order_id) : await foodApi.noPartnerRefund(order.order_id);
      onChange(u);
      toast.success(which === "pickup" ? "Switched to self-pickup — see your collection code" : "Full refund initiated");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl bg-amber-50 px-4 py-4 text-sm text-amber-900" data-testid="order-no-partner">
      <p className="font-semibold">No delivery partner is available right now</p>
      <p className="mt-1">
        {prepaid
          ? `Collect it yourself and we'll refund the ₹${fee.toFixed(0)} delivery fee${bonus > 0 ? ` plus ₹${bonus.toFixed(0)} off` : ""} — on us.`
          : `Collect it yourself and pay only ₹${payable.toFixed(0)} at the counter (₹${fee.toFixed(0)} delivery fee waived${bonus > 0 ? ` + ₹${bonus.toFixed(0)} off` : ""} — on us).`}
        {" "}Or get a full refund.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => act("pickup")} disabled={!!busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="no-partner-self-pickup">
          {busy === "pickup" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Pick it up myself {bonus > 0 || fee > 0 ? `(save ₹${(fee + bonus).toFixed(0)})` : ""}
        </Button>
        <Button variant="outline" onClick={() => act("refund")} disabled={!!busy} className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="no-partner-refund">
          {busy === "refund" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Get a full refund
        </Button>
      </div>
    </div>
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { replaceCart } = useCart();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loc, setLoc] = useState(null);
  const [restaurantPos, setRestaurantPos] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [reordering, setReordering] = useState(false);
  const isCustomer = ["rider", "customer"].includes(user?.role);
  const isRestaurant = user?.role === "restaurant";
  const terminal = order && ["delivered", "cancelled", "rejected"].includes(order.status);
  const isPickup = order?.order_type === "pickup";
  const [clock, setClock] = useState(Date.now());

  useEffect(() => {
    if (!isCustomer || !order?.restaurant_id) return;
    favApi.list().then((d) => setIsFav((d.ids || []).includes(order.restaurant_id))).catch(() => {});
  }, [isCustomer, order?.restaurant_id]);

  const toggleFav = async () => {
    if (!order?.restaurant_id) return;
    try {
      const res = isFav ? await favApi.remove(order.restaurant_id) : await favApi.add(order.restaurant_id);
      setIsFav(res.favourite);
      toast.success(res.favourite ? `Saved ${order.restaurant_name}` : `Removed ${order.restaurant_name}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const doReorder = async () => {
    setReordering(true);
    try {
      const res = await foodApi.reorder(order.order_id);
      replaceCart(res.restaurant, res.items);
      if (res.unavailable?.length) toast.info(`Unavailable now: ${res.unavailable.join(", ")}`);
      toast.success("Added to your cart");
      navigate("/app/checkout");
    } catch (e) { toast.error(errorMessage(e, "Could not reorder")); } finally { setReordering(false); }
  };
  useEffect(() => {
    const t = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const deliveryPt = order && Number.isFinite(order.delivery_lat) ? { lat: order.delivery_lat, lng: order.delivery_lng } : null;
  const driverLoc = loc && Number.isFinite(loc.lat) ? { lat: loc.lat, lng: loc.lng } : null;
  const deliveryRoad = useRoadRoute(driverLoc, deliveryPt, order?.status === "out_for_delivery" && !!driverLoc && !!deliveryPt);
  const deliveryEta = deliveryRoad.durationMin != null ? Math.max(1, Math.round(deliveryRoad.durationMin)) : loc?.eta_min;

  const arrivedRef = React.useRef(false);
  useEffect(() => {
    if (!isCustomer || order?.status !== "out_for_delivery") { arrivedRef.current = false; return; }
    if (deliveryEta != null && deliveryEta <= 1 && !arrivedRef.current) {
      arrivedRef.current = true;
      try { if (navigator.vibrate) navigator.vibrate([400, 150, 400]); } catch { /* ignore */ }
      toast.success("Your delivery is almost here!", { description: "The delivery partner is about a minute away." });
    }
  }, [deliveryEta, order?.status, isCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    foodApi.order(orderId).then(setOrder).catch(() => setError("Order not found"));
  }, [orderId]);

  useEffect(() => {
    if (!order || terminal) return undefined;
    const tick = () => {
      foodApi.order(orderId).then(setOrder).catch(() => {});
      if (order.driver) foodApi.driverLocation(orderId).then(setLoc).catch(() => {});
    };
    tick();
    const t = setInterval(tick, 6000);
    return () => clearInterval(t);
  }, [order?.status, orderId, terminal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!order?.restaurant_id) return;
    foodApi.restaurant(order.restaurant_id).then((d) => { const r = d.restaurant || d; if (Number.isFinite(r?.lat)) setRestaurantPos({ lat: r.lat, lng: r.lng }); }).catch(() => {});
  }, [order?.restaurant_id]);

  const rate = async (b) => { try { await foodApi.rateOrder(orderId, b); setOrder((o) => ({ ...o, rating: b.rating })); toast.success("Thanks for rating!"); } catch (e) { toast.error(errorMessage(e)); throw e; } };
  const markArrived = async () => {
    setArriving(true);
    try { const u = await foodApi.customerArrived(orderId); setOrder(u); toast.success("We've let the restaurant know you're here."); }
    catch (e) { toast.error(errorMessage(e)); } finally { setArriving(false); }
  };
  const shareEta = async (mins) => {
    setArriving(true);
    try { const u = await foodApi.customerEta(orderId, mins); setOrder(u); toast.success(`We've told the restaurant you'll arrive in ~${mins} min.`); }
    catch (e) { toast.error(errorMessage(e)); } finally { setArriving(false); }
  };

  if (error) return <EmptyState title={error} action={{ to: isRestaurant ? "/app/restaurant/orders" : "/app/orders", label: "Back to orders" }} />;
  if (!order) return <LoadingBlock rows={3} />;

  const back = isRestaurant ? "/app/restaurant/orders" : "/app/orders";

  return (
    <>
      <Link to={back} className="inline-flex items-center text-sm font-medium text-ink-soft hover:text-brand" data-testid="back-to-orders"><ArrowLeft className="mr-1.5 h-4 w-4" /> Orders</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Order {order.order_id.slice(-6).toUpperCase()}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink sm:text-3xl" data-testid="order-title">{isRestaurant ? order.customer_name : order.restaurant_name}</h1>
          <p className="mt-1 text-sm text-ink-soft">{formatDate(order.created_at)}</p>
        </div>
        <StatusBadge status={order.status} className="text-sm" />
      </div>
      <div className="mt-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${isPickup ? "bg-amber-100 text-amber-800" : "bg-brand-50 text-brand"}`} data-testid="order-type-badge">
          {isPickup ? <><Store className="h-3.5 w-3.5" /> Self pick-up</> : <><Bike className="h-3.5 w-3.5" /> Delivery</>}
        </span>
      </div>
      {isCustomer && order.delivery_arrived_at && order.status === "out_for_delivery" ? (
        <div className="mt-3" data-testid="customer-arrived-banner">
          <ArrivedWaiting arrivedAt={order.delivery_arrived_at} who="Your delivery partner" testid="customer-arrived-waiting" />
        </div>
      ) : isCustomer && !terminal && order.driver_eta_at && (Date.now() - new Date(order.driver_eta_at).getTime() < 15 * 60 * 1000) && (
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm font-semibold text-green-800" data-testid="driver-eta-banner">
          <Bike className="h-4 w-4" /> Your delivery partner is {order.driver_eta_min > 0 ? `arriving in ~${order.driver_eta_min} min` : "arriving now"}.
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <Section title="Progress">
            {order.status === "awaiting_payment" ? (
              <AwaitingPayment order={order} onChange={setOrder} user={user} isCustomer={isCustomer} />
            ) : order.status === "rejected" ? (
              <div className="rounded-xl bg-red-50 px-4 py-4 text-sm text-red-700" data-testid="order-rejected">
                <p className="font-semibold">{order.auto_rejected ? "Order auto-cancelled" : "This order was declined by the restaurant."}</p>
                <p className="mt-1" data-testid="order-cancelledby">{order.cancel_info?.text || (order.auto_rejected ? `${order.restaurant_name} didn't respond in time, so we automatically cancelled your order.` : (order.rejected_reason ? `Reason: ${order.rejected_reason}` : ""))}</p>
                {isCustomer && (
                  <Button asChild className="mt-3 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="reorder-elsewhere"><Link to="/app/food">Order from another restaurant <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
                )}
              </div>
            ) : order.status === "cancelled" ? (
              <div className="rounded-xl bg-red-50 px-4 py-4 text-sm text-red-700" data-testid="order-cancelled">
                <p className="font-semibold">Order cancelled</p>
                <p className="mt-1" data-testid="order-cancel-reason">{order.cancel_info?.text || order.cancelled_reason || order.rejected_reason || "This order was cancelled."}</p>
              </div>
            ) : order.status === "no_partner" && isCustomer ? (
              <NoPartnerChoice order={order} onChange={setOrder} />
            ) : <Stepper steps={isPickup ? PICKUP_ORDER_STEPS : ORDER_STEPS} current={order.status} />}
            {isCustomer && order.refund_tracker && <div className="mt-4"><RefundTracker tracker={order.refund_tracker} /></div>}
            {(() => {
              const live = order.ready_eta_at && ["accepted", "preparing"].includes(order.status);
              const selfPickup = isPickup || order.self_pickup_switch;
              if (live && selfPickup) {
                const msLeft = new Date(order.ready_eta_at).getTime() - clock;
                const ready = msLeft <= 0;
                const mm = Math.floor(Math.max(0, msLeft) / 60000);
                const ss = Math.floor((Math.max(0, msLeft) % 60000) / 1000);
                const clk = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
                return (
                  <div className={`mt-4 rounded-xl px-4 py-3 ${ready ? "bg-green-100 text-green-800" : "bg-amber-50 text-amber-800"}`} data-testid="pickup-countdown">
                    <p className="flex items-center gap-2 text-sm font-semibold"><Store className="h-4 w-4" /> {ready ? "Ready to collect now" : "Ready to collect in"} {!ready && <span className="font-mono text-base tabular-nums" data-testid="pickup-countdown-clock">{clk}</span>}</p>
                    <p className="mt-0.5 text-xs">{ready ? `Head over and show code ${order.pickup_code || ""} at the counter.` : `Show code ${order.pickup_code || ""} at the counter when you arrive.`}</p>
                  </div>
                );
              }
              const mins = live ? Math.max(0, Math.round((new Date(order.ready_eta_at).getTime() - clock) / 60000)) : null;
              if (live) return (
                <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800" data-testid="ready-countdown"><Store className="h-4 w-4" /> {isPickup ? "Ready for pickup" : "Ready"} in ~{mins} min{order.prep_min ? ` (restaurant set ${order.prep_min} min)` : ""}</p>
              );
              if (isPickup && order.status === "placed" && order.pickup_prep_min) return (
                <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="pickup-eta"><Store className="h-4 w-4" /> Usually ready in ~{order.pickup_prep_min} min — collect from the restaurant counter.</p>
              );
              return null;
            })()}
            {isPickup && isRestaurant && !["delivered", "cancelled", "rejected"].includes(order.status) && (order.customer_arrived
              ? <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-green-100 px-3 py-2 text-sm font-semibold text-green-800" data-testid="customer-arrived-badge"><MapPin className="h-4 w-4" /> Customer has arrived to collect</p>
              : order.customer_eta_min ? <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800" data-testid="customer-eta-badge"><Navigation className="h-4 w-4" /> Customer arriving in ~{order.customer_eta_min} min</p> : null
            )}
            {order.status === "out_for_delivery" && loc && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-graphite-50 px-3 py-2 text-sm text-graphite" data-testid="driver-progress"><Bike className="h-4 w-4" /> {deliveryEta != null ? <span className="font-semibold">Arriving in ~{deliveryEta} min</span> : `Delivery partner ${titleCase(loc.phase)}`} · {Math.round((loc.progress || 0) * 100)}% of the way</p>
            )}
            {isRestaurant && <div className="mt-5"><OrderStatusControls order={order} onChange={setOrder} /></div>}
          </Section>
          {order.delivery_driver_id && order.driver && !terminal && (
            <Section title="Live delivery tracking" description={loc ? (deliveryEta != null ? `Arriving in ~${deliveryEta} min · ${Math.round((loc.progress || 0) * 100)}% of the route` : `Rider ${titleCase(loc.phase)} · ${Math.round((loc.progress || 0) * 100)}% of the route`) : "Waiting for delivery partner location…"}>
              <RideMap
                pickup={restaurantPos}
                drop={Number.isFinite(order.delivery_lat) ? { lat: order.delivery_lat, lng: order.delivery_lng } : null}
                activeDriver={{ ...order.driver, lat: loc?.lat ?? order.driver.lat, lng: loc?.lng ?? order.driver.lng }}
                route={deliveryRoad.points.length >= 2 ? deliveryRoad.points : (loc && deliveryPt && order.status === "out_for_delivery" ? [{ lat: loc.lat, lng: loc.lng }, deliveryPt] : null)}
                traffic={deliveryRoad.traffic}
                follow
                className="h-[360px]"
              />
            </Section>
          )}
          {order.status_events?.length > 0 && (
            <Section title="Order updates" description="Everything that's happened with your order, newest first">
              <ol className="space-y-4" data-testid="order-updates-feed">
                {[...order.status_events].reverse().map((e, i) => (
                  <li key={`${e.status}-${e.at}`} className="flex gap-3" data-testid={`order-update-${e.status}`}>
                    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${["cancelled", "rejected"].includes(e.status) ? "bg-red-100 text-red-600" : i === 0 ? "bg-brand text-white" : "bg-brand-50 text-brand"}`}>
                      {["cancelled", "rejected"].includes(e.status) ? <span className="text-sm font-bold">!</span> : <CheckCircle2 className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${i === 0 ? "text-ink" : "text-ink-soft"}`}>{e.label}</p>
                      <p className="text-xs text-ink-muted">{formatDate(e.at)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>
          )}

          <Section title="Items">
            <ul className="divide-y divide-surface-200 text-sm">
              {order.items.map((i, idx) => (
                <li key={`${i.item_id}-${idx}`} className="flex items-center justify-between py-3">
                  <span className="flex items-center gap-2 text-ink"><span className={`h-3 w-3 rounded-sm border-2 ${i.veg ? "border-graphite" : "border-red-600"}`} />{i.qty} × {i.name}</span>
                  <span className="font-medium text-ink">{inr(i.line_total ?? i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-1.5 border-t border-surface-200 pt-4 text-sm">
              <div className="flex justify-between"><dt className="text-ink-soft">Subtotal</dt><dd>{inr(order.subtotal)}</dd></div>
              {order.packaging_fee > 0 && <div className="flex justify-between"><dt className="text-ink-soft">Packaging fee</dt><dd data-testid="order-packaging-fee">{inr(order.packaging_fee)}</dd></div>}
              <div className="flex justify-between"><dt className="text-ink-soft">Delivery fee</dt><dd>{inr(order.delivery_fee)}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-soft">Tax</dt><dd>{inr(order.tax)}</dd></div>
              {order.discount > 0 && <div className="flex justify-between text-graphite"><dt>Discount {order.promo?.code ? `(${order.promo.code})` : ""}</dt><dd>-{inr(order.discount)}</dd></div>}
              <div className="flex justify-between border-t border-surface-200 pt-2 text-base font-bold text-ink"><dt>Total</dt><dd data-testid="order-total">{inr(order.total)}</dd></div>
            </dl>
          </Section>
        </div>
        <div className="space-y-6">
          <Section title={isPickup ? "Pick-up" : "Delivery"}>
            <p className="flex items-start gap-2 text-sm text-ink"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{isPickup ? `Collect from ${order.restaurant_name}` : order.delivery_address}</p>
            {isPickup && order.restaurant_address && <p className="mt-1 flex items-start gap-2 text-xs text-ink-soft"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-muted" />{order.restaurant_address}</p>}
            {isCustomer && isPickup && order.pickup_code && !["cancelled", "rejected"].includes(order.status) && (
              <div className="mt-3 rounded-xl border border-dashed border-brand/40 bg-brand-50 px-4 py-3 text-center" data-testid="pickup-code">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Collection code</p>
                <p className="mt-0.5 text-3xl font-black tracking-[0.3em] text-brand" data-testid="pickup-code-value">{order.pickup_code}</p>
                <p className="mt-1 text-xs text-ink-soft">Show this at the counter to collect your order.</p>
                {(order.restaurant_lat != null || order.restaurant_phone) && (
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {order.restaurant_lat != null && <Button asChild className="rounded-full bg-brand text-white hover:bg-brand-600"><a href={`https://www.google.com/maps/dir/?api=1&destination=${order.restaurant_lat},${order.restaurant_lng}&travelmode=driving`} target="_blank" rel="noreferrer" data-testid="pickup-navigate-restaurant"><Navigation className="mr-2 h-4 w-4" /> Navigate to restaurant</a></Button>}
                    {order.restaurant_phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${order.restaurant_phone}`} data-testid="pickup-call-restaurant"><Phone className="mr-2 h-4 w-4" /> Call restaurant</a></Button>}
                  </div>
                )}
              </div>
            )}
            {isRestaurant && <p className="mt-2 text-sm text-ink-soft">Customer: {order.customer_name} {order.customer_phone && <a href={`tel:${order.customer_phone}`} className="text-brand font-semibold" data-testid="order-rest-call-customer">{order.customer_phone}</a>}</p>}
            {isRestaurant && !terminal && order.driver?.phone && <p className="mt-1 text-sm text-ink-soft">Delivery partner: {order.driver.name} <a href={`tel:${order.driver.phone}`} className="text-brand font-semibold" data-testid="order-rest-call-driver">{order.driver.phone}</a></p>}
            {order.notes && <p className="mt-3 rounded-xl bg-surface-100 p-3 text-sm text-ink-soft">Note: {order.notes}</p>}
            <p className="mt-3 text-xs text-ink-muted">Payment: {order.payment_method ? titleCase(order.payment_method) : (isPickup ? "at restaurant" : "on delivery")} · <span className="font-semibold uppercase">{order.payment_status}</span></p>
          </Section>
          {isCustomer && (order.restaurant_address || order.restaurant_lat != null || order.restaurant_phone) && (
            <Section title="Restaurant">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-ink" data-testid="order-restaurant-name">{order.restaurant_name}</p>
                <button onClick={toggleFav} className={`flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${isFav ? "border-rose-200 bg-rose-50 text-rose-600" : "border-surface-300 text-ink-soft hover:bg-surface-100"}`} data-testid="order-fav-toggle">
                  <Heart className={`h-3.5 w-3.5 ${isFav ? "fill-rose-500 text-rose-500" : ""}`} /> {isFav ? "Saved" : "Save"}
                </button>
              </div>
              {order.restaurant_open != null && (
                <p className="mt-1 flex items-center gap-2 text-sm" data-testid="order-restaurant-status">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${order.restaurant_open ? "bg-green-100 text-green-700" : "bg-surface-200 text-ink-soft"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${order.restaurant_open ? "bg-green-500" : "bg-ink-muted"}`} />
                    {order.restaurant_open ? "Open now" : (order.restaurant_status_label || "Closed")}
                  </span>
                </p>
              )}
              {order.restaurant_address && <p className="mt-1 flex items-start gap-2 text-sm text-ink-soft"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{order.restaurant_address}</p>}
              {order.restaurant_hours && <OpeningHours hours={order.restaurant_hours} />}
              <div className="mt-3 flex flex-col gap-2">
                <Button onClick={() => navigate(`/app/chat/${order.order_id}`)} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="order-message-restaurant"><MessageCircle className="mr-2 h-4 w-4" /> Message restaurant</Button>
                {terminal && <Button onClick={doReorder} disabled={reordering} variant="outline" className="rounded-full" data-testid="order-reorder">{reordering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />} Reorder</Button>}
                {order.restaurant_phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${order.restaurant_phone}`} data-testid="order-call-restaurant"><Phone className="mr-2 h-4 w-4" /> Call restaurant</a></Button>}
                {order.restaurant_lat != null && <Button asChild variant="outline" className="rounded-full"><a href={`https://www.google.com/maps/dir/?api=1&destination=${order.restaurant_lat},${order.restaurant_lng}&travelmode=driving`} target="_blank" rel="noreferrer" data-testid="order-navigate-restaurant"><Navigation className="mr-2 h-4 w-4" /> Get directions</a></Button>}
              </div>
            </Section>
          )}
          {order.delivery_driver_id && order.driver && (
            <Section title="Delivery partner">
              <DriverCard driver={order.driver} compact />
            </Section>
          )}
          <Section title="Actions">
            <div className="flex flex-col gap-2">
              {isCustomer && isPickup && ["accepted", "preparing", "ready"].includes(order.status) && (
                order.customer_arrived
                  ? <p className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-2 text-sm font-semibold text-green-800" data-testid="arrived-confirmed"><MapPin className="h-4 w-4" /> You're checked in — the restaurant knows you're here</p>
                  : <div className="flex flex-col gap-2" data-testid="arrival-eta-picker">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft"><Navigation className="h-4 w-4" /> On my way in</span>
                        {[5, 10, 15, 20].map((m) => (
                          <button key={m} type="button" onClick={() => shareEta(m)} disabled={arriving} className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${order.customer_eta_min === m ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:bg-surface-200"}`} data-testid={`eta-${m}`}>{m} min</button>
                        ))}
                      </div>
                      {order.customer_eta_min && <p className="text-xs font-medium text-blue-700" data-testid="eta-shared">You told them you'll arrive in ~{order.customer_eta_min} min.</p>}
                      <Button onClick={markArrived} disabled={arriving} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="order-im-here">{arriving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<MapPin className="mr-2 h-4 w-4" /> I'm here</Button>
                    </div>
              )}
              {isCustomer && order.restaurant_phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${order.restaurant_phone}`} data-testid="order-call-restaurant"><Phone className="mr-2 h-4 w-4" /> Call restaurant · {order.restaurant_phone}</a></Button>}
              {isCustomer && !terminal && order.status !== "no_partner" && order.driver?.phone && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${order.driver.phone}`} data-testid="order-call-driver"><Phone className="mr-2 h-4 w-4" /> Call driver · {order.driver.phone}</a></Button>}
              {isCustomer && !terminal && order.status !== "no_partner" && order.driver && !order.driver.phone && <Button asChild variant="outline" className="rounded-full"><Link to={`/app/chat/${order.order_id}`} data-testid="order-chat-driver"><MessageCircle className="mr-2 h-4 w-4" /> Chat with driver (no phone on file)<ChatUnreadBadge chatId={order.order_id} /></Link></Button>}
              {order.status !== "no_partner" && <Button asChild variant="outline" className="rounded-full"><Link to={`/app/chat/${order.order_id}`} data-testid="order-chat"><MessageCircle className="mr-2 h-4 w-4" /> Open chat<ChatUnreadBadge chatId={order.order_id} /></Link></Button>}
              <Button asChild variant="outline" className="rounded-full"><Link to={`/app/receipts/order/${order.order_id}`} data-testid="order-receipt"><Receipt className="mr-2 h-4 w-4" /> View receipt</Link></Button>
              {isCustomer && order.status !== "cancelled" && <ReportDialog contextType="order" contextId={order.order_id} />}
              {isCustomer && order.payment_method !== "cash" && order.payment_status !== "paid" && order.status !== "cancelled" && <Button onClick={() => setPayOpen(true)} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="order-pay"><Wallet className="mr-2 h-4 w-4" /> Pay {inr(order.total)}</Button>}
              {isCustomer && (order.payment_method || "cash") === "cash" && order.payment_status !== "paid" && !["cancelled", "rejected", "awaiting_payment"].includes(order.status) && <Button onClick={() => setPayOpen(true)} variant="outline" className="rounded-full" data-testid="order-pay-online"><Wallet className="mr-2 h-4 w-4" /> Pay {inr(order.total)} online instead</Button>}
              {isCustomer && order.status === "delivered" && !order.rating && <Button onClick={() => setRateOpen(true)} variant="outline" className="rounded-full" data-testid="order-rate"><Star className="mr-2 h-4 w-4" /> Rate order</Button>}
              {order.rating && <p className="inline-flex items-center gap-1 text-sm text-ink-soft"><Star className="h-4 w-4 fill-brand text-brand" /> Rated {order.rating}/5</p>}
              {isCustomer && ["placed", "awaiting_payment"].includes(order.status) && <CustomerCancel order={order} onChange={setOrder} />}
              {isCustomer && !terminal && !["placed", "awaiting_payment", "no_partner"].includes(order.status) && (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="customer-cancel-blocked">
                  The restaurant has accepted this order, so it can no longer be cancelled here. To cancel, please call the restaurant{order.restaurant_phone ? ` on ${order.restaurant_phone}` : ""}.
                </p>
              )}
            </div>
          </Section>
        </div>
      </div>
      <PayDialog open={payOpen} onOpenChange={setPayOpen} amount={order.total} kind="order" refId={orderId} onPaid={setOrder} description={`Order from ${order.restaurant_name || "restaurant"}`} />
      <RateDialog open={rateOpen} onOpenChange={setRateOpen} title="Rate your order" subRatings onRate={rate} />
    </>
  );
}
